import { useState } from 'react';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { MapPin, Phone, Mail, Clock, Send, Building, CheckCircle, AlertCircle } from 'lucide-react';
import { Alert, AlertDescription } from './ui/alert';
// EmailJS will be imported dynamically

interface ContactDetail {
  text: string;
  link?: string;
}

export function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    subject: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [submittedEmail, setSubmittedEmail] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      // Send to backend API
      const response = await fetch('http://localhost:3001/api/contact', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Success! Save submitted email, then reset form and show success message
        setSubmittedEmail(formData.email);
        setFormData({
          name: '',
          email: '',
          company: '',
          phone: '',
          subject: '',
          message: ''
        });
        
        setSubmitStatus('success');
        
        // Scroll to success message
        setTimeout(() => {
          document.getElementById('submit-status')?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
        }, 100);
      } else {
        throw new Error(data.message || 'Failed to send message');
      }
    } catch (error) {
      console.error('Error sending email:', error);
      
      // Fallback to mailto if backend fails
      const subject = encodeURIComponent(formData.subject);
      const body = encodeURIComponent(
        `Name: ${formData.name}\n` +
        `Email: ${formData.email}\n` +
        `Company: ${formData.company}\n` +
        `Phone: ${formData.phone}\n\n` +
        `Message:\n${formData.message}`
      );
      
      // Open mailto link
      window.location.href = `mailto:info@doubleclickfze.com?subject=${subject}&body=${body}`;
      
      // Show info message about fallback
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const contactInfo: Array<{ icon: JSX.Element; title: string; details: ContactDetail[] }> = [
    {
      icon: <Building className="w-6 h-6 text-[#fe2b32]" />,
      title: "Company",
      details: [
        { text: "DOUBLE CLICK TRADING FZE" },
        { text: "Dubai, United Arab Emirates" },
        { text: "Trade License: 34666" }
      ]
    },
    {
      icon: <MapPin className="w-6 h-6 text-[#ffc533]" />,
      title: "Address",
      details: [
        { text: "Office - C1 - 1F - SF0864" },
        { text: "Ajman Free Zone C1 Building" },
        { text: "Dubai, UAE" }
      ]
    },
    {
      icon: <Phone className="w-6 h-6 text-[#ff41a5]" />,
      title: "Phone",
      details: [
        { text: "+971 54 364 3476 (Primary)", link: "tel:+971543643476" },
        { text: "WhatsApp: +971 54 364 3476", link: "https://wa.me/971543643476" }
      ]
    },
    {
      icon: <Mail className="w-6 h-6 text-[#fe2b32]" />,
      title: "Email",
      details: [
        { text: "info@doubleclickfze.com", link: "mailto:info@doubleclickfze.com" },
        { text: "operations@doubleclickfze.com", link: "mailto:operations@doubleclickfze.com" }
      ]
    },
    {
      icon: <Clock className="w-6 h-6 text-[#ffc533]" />,
      title: "Business Hours",
      details: [
        { text: "Monday – Friday: 11:00 AM to 8:00 PM" },
        { text: "Saturday & Sunday: Closed" }
      ]
    }
  ];

  const offices = [
    {
      location: "Dubai Headquarters",
      address: "Office - C1 - 1F - SF0864, Ajman Free Zone C1 Building, Dubai, UAE",
      phone: "+971 54 364 3476",
      email: "dubai@doubleclickfze.com"
    }
  ];

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-r from-gray-900 to-gray-800 text-white">
        <div className="container mx-auto px-4">
          <div className="text-center">
            <h1 className="text-5xl font-bold mb-6">Contact Us</h1>
            <p className="text-xl max-w-3xl mx-auto text-gray-200">
              Get in touch with our experienced team for all your scrap trading needs. 
              We're here to help you find the best solutions for your business.
            </p>
          </div>
        </div>
      </section>

      {/* Contact Form and Info */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div>
              <h2 className="text-3xl font-bold mb-4">Send Us a Message</h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you within 24 hours. 
                All inquiries are sent directly to our team at info@doubleclickfze.com
              </p>
              
              {/* Success/Error Messages */}
              <div id="submit-status" className="mb-6">
                {submitStatus === 'success' && (
                  <Alert className="bg-green-50 border-green-200">
                    <CheckCircle className="h-5 w-5 text-green-600" />
                    <AlertDescription className="text-green-800">
                      <strong>Message sent successfully!</strong> Thank you for contacting us. 
                      We've received your inquiry and will respond to you at <strong>{submittedEmail}</strong> within 24 hours.
                    </AlertDescription>
                  </Alert>
                )}
                
                {submitStatus === 'error' && (
                  <Alert className="bg-blue-50 border-blue-200">
                    <AlertCircle className="h-5 w-5 text-blue-600" />
                    <AlertDescription className="text-blue-800">
                      <strong>Opening your email client...</strong> Your default email application will open with a pre-filled message to info@doubleclickfze.com. 
                      Simply click send to submit your inquiry.
                    </AlertDescription>
                  </Alert>
                )}
              </div>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="name">Full Name *</Label>
                    <Input
                      id="name"
                      name="name"
                      type="text"
                      value={formData.name}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="email">Email Address *</Label>
                    <Input
                      id="email"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      required
                      className="mt-1"
                    />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <Label htmlFor="company">Company Name</Label>
                    <Input
                      id="company"
                      name="company"
                      type="text"
                      value={formData.company}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="phone">Phone Number</Label>
                    <Input
                      id="phone"
                      name="phone"
                      type="tel"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="mt-1"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="subject">Subject *</Label>
                  <Input
                    id="subject"
                    name="subject"
                    type="text"
                    value={formData.subject}
                    onChange={handleInputChange}
                    required
                    className="mt-1"
                  />
                </div>

                <div>
                  <Label htmlFor="message">Message *</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleInputChange}
                    required
                    rows={6}
                    className="mt-1"
                    placeholder="Please provide details about your requirements, including material types, quantities, and preferred delivery terms."
                  />
                </div>

                <Button 
                  type="submit" 
                  size="lg" 
                  className="w-full bg-[#fe2b32] hover:bg-red-600"
                  disabled={isSubmitting}
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                  <Send className="w-5 h-5 ml-2" />
                </Button>
              </form>
            </div>

            {/* Contact Information */}
            <div>
              <h2 className="text-3xl font-bold mb-8">Get In Touch</h2>
              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <Card key={index} className="hover:shadow-lg transition-shadow">
                    <CardContent className="p-6">
                      <div className="flex items-start">
                        <div className="mr-4 mt-1">{info.icon}</div>
                        <div>
                          <h3 className="font-semibold text-lg mb-2">{info.title}</h3>
                          <div className="space-y-1">
                            {info.details.map((detail, detailIndex) => (
                              detail.link ? (
                                <a 
                                  key={detailIndex} 
                                  href={detail.link}
                                  className="text-gray-600 hover:text-[#fe2b32] transition-colors block"
                                  target={detail.link.startsWith('https') ? '_blank' : undefined}
                                  rel={detail.link.startsWith('https') ? 'noopener noreferrer' : undefined}
                                >
                                  {detail.text}
                                </a>
                              ) : (
                                <p key={detailIndex} className="text-gray-600">{detail.text}</p>
                              )
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Office Locations */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold mb-4">Our Location</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Strategically located in Dubai to serve clients across the globe.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-1 gap-8 max-w-2xl mx-auto">
            {offices.map((office, index) => (
              <Card key={index} className="hover:shadow-lg transition-shadow">
                <CardContent className="p-8 text-center">
                  <h3 className="text-2xl font-semibold mb-4">{office.location}</h3>
                  <div className="space-y-3">
                    <div className="flex items-center justify-center">
                      <MapPin className="w-5 h-5 text-[#fe2b32] mr-2" />
                      <span className="text-gray-600">{office.address}</span>
                    </div>
                    <a href="tel:+971543643476" className="flex items-center justify-center hover:text-[#fe2b32] transition-colors">
                      <Phone className="w-5 h-5 text-[#ffc533] mr-2" />
                      <span className="text-gray-600">{office.phone}</span>
                    </a>
                    <a href="mailto:dubai@doubleclickfze.com" className="flex items-center justify-center hover:text-[#fe2b32] transition-colors">
                      <Mail className="w-5 h-5 text-[#ff41a5] mr-2" />
                      <span className="text-gray-600">{office.email}</span>
                    </a>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Map */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold mb-4">Find Us</h2>
            <p className="text-gray-600">Located in Ajman Free Zone</p>
          </div>
          
          <div className="max-w-4xl mx-auto">
            <div className="rounded-lg overflow-hidden shadow-lg">
              <iframe 
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3603.5728178503236!2d55.450087075935286!3d25.419106122675828!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3e5f5784a7c97153%3A0x2f7b6f7ff376bcb3!2sAjman%20Free%20Zone!5e0!3m2!1sen!2sin!4v1760001825562!5m2!1sen!2sin" 
                width="100%" 
                height="450" 
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy" 
                referrerPolicy="no-referrer-when-downgrade"
                title="Ajman Free Zone Location"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-20 bg-gradient-to-r from-[#fe2b32] to-[#ff41a5] text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold mb-6">Ready to Start Trading?</h2>
          <p className="text-xl mb-8 max-w-2xl mx-auto">
            Join hundreds of satisfied clients who trust DOUBLE CLICK TRADING FZE 
            for their scrap trading needs.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button 
              size="lg" 
              variant="secondary" 
              className="bg-white text-[#fe2b32] hover:bg-gray-100"
              asChild
            >
              <a href="tel:+971543643476">Call Now: +971 54 364 3476</a>
            </Button>
            <Button 
              size="lg" 
              variant="outline" 
              className="border-white text-white hover:bg-white hover:text-[#fe2b32]"
              asChild
            >
              <a href="mailto:info@doubleclickfze.com">Email: info@doubleclickfze.com</a>
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
}